/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2025, Zumper
 */

import { APP_NAME } from '@zcode/env'
import {
  APP_HOSTNAME,
  IS_ZUMPER_RENTALS,
  keepBetaParamTo,
} from '@zcode/nr-utils'
import { addServerSelectParam, isTruthy } from '@zcode/utils'
import { type Path, createPath } from 'react-router-dom-v5-compat'
import invariant from 'tiny-invariant'
import { AuthUrl } from './types'

const ZUMPER_ORIGIN = `https://${APP_HOSTNAME.replace(
  /zumperrentals/,
  'zumper'
)}`

function assertIsAuthUrl<Type extends 'signin' | 'signup'>(
  url: string,
  type: Type
): asserts url is AuthUrl<Type> {
  invariant(
    url
      .replace(/^https:\/\/\w+\.zumper\.com/, '')
      .startsWith(`/zumper-account/${type}`) ||
      url.startsWith(`/padmapper-account/${type}`)
  )
}

const getAuthUrl =
  <Type extends 'signin' | 'signup'>(type: Type) =>
  (
    location: Partial<Path>,
    {
      direct_listing_created,
      direct_listing_opt_in,
      email,
      first_name,
      last_name,
      manage_enabled,
      next,
      portal_token,
      referrer,
    }: {
      email?: string | null
      next?: string
      referrer?:
        | 'favorite' // Z & PM
        | 'for_you' // Z
        | 'max_searches' // Z
        | 'message' // Z
        | 'message_existing_user' // Z
        | 'search' // Z & PM
        | 'search_existing_user' // Z
        | 'existing_user' // PM
    } & ('signup' extends Type
      ? {
          direct_listing_created?: boolean
          direct_listing_opt_in?: boolean
          first_name?: string | null | undefined
          last_name?: string | null | undefined
          manage_enabled?: boolean
          portal_token?: string | null
        }
      : {
          direct_listing_created?: undefined
          direct_listing_opt_in?: undefined
          first_name?: undefined
          last_name?: undefined
          manage_enabled?: undefined
          portal_token?: undefined
        }) = {}
  ): AuthUrl<Type> => {
    const currentUrl = createPath(location)
    const completeCurrentUrl = `https://${APP_HOSTNAME}${currentUrl}`

    const urlSearchParams = new URLSearchParams(
      [
        direct_listing_created && [
          'direct_listing_created',
          `${direct_listing_created}`,
        ],
        direct_listing_opt_in && [
          'direct_listing_opt_in',
          `${direct_listing_opt_in}`,
        ],
        email && ['email', email],
        first_name && ['first_name', first_name],
        last_name && ['last_name', last_name],
        manage_enabled && ['manage_enabled', `${manage_enabled}`],
        [
          'next',
          new URL(next || currentUrl, completeCurrentUrl)
            .toString()
            .replace(
              IS_ZUMPER_RENTALS ? ZUMPER_ORIGIN : `https://${APP_HOSTNAME}`,
              ''
            ),
        ],
        portal_token && ['portal_token', portal_token],
        referrer && ['referrer', referrer],
      ].filter(isTruthy)
    )
    invariant(APP_NAME, 'APP_NAME is required')
    const isPro = APP_NAME === 'Pro'
    let href =
      (IS_ZUMPER_RENTALS ? ZUMPER_ORIGIN : '') +
      createPath(
        keepBetaParamTo(
          {
            pathname: `/${
              isPro ? 'zumper' : APP_NAME.toLowerCase()
            }-account/${type}`,
            search: urlSearchParams.toString(),
          },
          location
        )
      )
    const url = new URL(href, completeCurrentUrl)
    if (!isPro) {
      addServerSelectParam(url)
    }
    href = url.toString()
    if (url.hostname === APP_HOSTNAME) {
      href = href.substring(url.origin.length)
    }
    assertIsAuthUrl(href, type)
    return href
  }

export const getSignInUrl = getAuthUrl('signin')
export const getSignUpUrl = getAuthUrl('signup')
